// Overview view-models (DC91), pure: the analytics slice + queue lists in, display
// strings out. Transcribes the design's `overviewVals()` (the admin export (Super Admin .dc.html)
// D:9031-9350) onto the API-207 shape; which export figures have no contract field, and
// which deferred cards were swapped, is recorded in the DC91 report.
import type { Lang } from '@bookingdei/i18n/translate';
import type { MessageKey } from '@bookingdei/i18n/message-key';
import { formatMessage } from '@bookingdei/i18n/interpolate';
import type { MessageParams } from '@bookingdei/i18n/interpolate';
import { formatNumber, localiseDigits } from '@bookingdei/i18n/format-number';
import { monthName } from '@bookingdei/i18n/format-date';
import type { OverviewAnalytics, OverviewKpiId } from '../../mock/seeds/admin-overview-seed';

export interface Fmt {
  lang: Lang;
  t: (key: MessageKey) => string;
  /** Money in minor units, lakh/crore compaction at two decimals (DR-49). */
  money: (minor: number) => string;
  now: Date;
}

export const msg = (f: Fmt, key: MessageKey, params: MessageParams = {}) =>
  formatMessage(f.lang, key, params);
export const num = (f: Fmt, n: number, maxFractionDigits = 2) =>
  formatNumber(f.lang, n, { maxFractionDigits });

// ---------------------------------------------------------------- small pure pieces

/** Design `spark()`: 100×40 viewBox, 4-unit pad, cubic mid-point smoothing. */
export function sparkPaths(values: number[]): {
  line: string;
  area: string;
  dotX: string;
  dotY: string;
} {
  const W = 100;
  const H = 40;
  const pad = 4;
  const v = values.length > 1 ? values : [1, 1];
  const min = Math.min(...v);
  const max = Math.max(...v);
  const span = max - min || 1;
  const pts = v.map(
    (x, i) =>
      [
        pad + (i * (W - pad * 2)) / (v.length - 1),
        H - pad - ((x - min) / span) * (H - pad * 2),
      ] as const,
  );
  const f = (n: number) => n.toFixed(1);
  let d = `M ${f(pts[0]![0])} ${f(pts[0]![1])}`;
  for (let i = 1; i < pts.length; i++) {
    const [px, py] = pts[i - 1]!;
    const [cx, cy] = pts[i]!;
    const mx = (px + cx) / 2;
    d += ` C ${f(mx)} ${f(py)}, ${f(mx)} ${f(cy)}, ${f(cx)} ${f(cy)}`;
  }
  const last = pts[pts.length - 1]!;
  return {
    line: d,
    area: `${d} L ${f(last[0])} ${H} L ${f(pts[0]![0])} ${H} Z`,
    dotX: f(last[0]),
    dotY: f(last[1]),
  };
}

/** Design `segs()`: how many of five pills a share fills (at least one). */
export function filledSegments(pct: number): number {
  return Math.max(1, Math.round((Math.max(0, Math.min(100, pct)) / 100) * 5));
}

/** Design `splitNum()`: currency prefix, number, unit suffix ("৳", "15.46", " L"). */
export function splitNumber(v: string): { pre: string; main: string; suf: string } {
  const m = /^([^\d০-৯\-−]*)([\d০-৯.,\-−]+)(.*)$/.exec(v);
  return m
    ? { pre: m[1] ?? '', main: m[2] ?? v, suf: (m[3] ?? '').trim() }
    : { pre: '', main: v, suf: '' };
}

export type Rate = 'strong' | 'steady' | 'down' | 'flat';

/** Design trend word: none → Flat, negative → Down, ≥ 5 → Strong, else Steady. */
export function trendRate(p: number | null): Rate {
  if (p === null) return 'flat';
  if (p < 0) return 'down';
  return p >= 5 ? 'strong' : 'steady';
}

/** Month-on-month change in percent, one decimal; null without a base. */
export function pctChange(now: number, prev: number): number | null {
  if (!prev) return null;
  return Math.round(((now - prev) / prev) * 1000) / 10;
}

const signed = (f: Fmt, n: number, maxFractionDigits = 1) =>
  (n < 0 ? '' : '+') + num(f, n, maxFractionDigits);

/** "Jan – Jul 2026": January to the current month of the clock's year. */
export function yearToDate(f: Fmt): string {
  return msg(f, 'admin.overview.ytd_range', {
    from: monthName(f.lang, 0),
    to: monthName(f.lang, f.now.getMonth()),
    year: localiseDigits(f.lang, f.now.getFullYear()),
  });
}

/** Last `n` values of one monthly series, oldest first. */
export function seriesOf(a: Pick<OverviewAnalytics, 'items'>, metric: string, n = 7): number[] {
  return (a.items ?? [])
    .filter((i) => i.metric === metric)
    .sort((x, y) => x.day.localeCompare(y.day))
    .slice(-n)
    .map((i) => i.value);
}

// ---------------------------------------------------------------- KPI cards

export type KpiIcon = 'card' | 'currency' | 'calendar' | 'store' | 'tag' | 'chart';

export interface KpiStat {
  v: string;
  k: string;
  s: string;
}

export interface KpiCard {
  id: OverviewKpiId;
  icon: KpiIcon;
  label: string;
  value: string;
  sideV?: string;
  sideS?: string;
  delta?: { text: string; up: boolean };
  note?: string;
  stats: KpiStat[];
  sub?: string;
  rate: Rate;
  spark: number[];
  /** The breakdown series is money (minor units) rather than a count. */
  money: boolean;
}

const RATE_KEY: Record<Rate, MessageKey> = {
  strong: 'admin.overview.rate.strong',
  steady: 'admin.overview.rate.steady',
  down: 'admin.overview.rate.down',
  flat: 'admin.overview.rate.flat',
};
export const rateLabel = (f: Fmt, r: Rate) => f.t(RATE_KEY[r]);

/** The six Overview cards, hero first (design order; Ads Manager → GMV per DR-43/FR-A12). */
export function buildKpis(a: OverviewAnalytics, f: Fmt): KpiCard[] {
  const k = a.kpis;
  const pct = (n: number | null) => (n === null ? '' : `${signed(f, n)}%`);
  const vsLast = (n: number | null) =>
    n === null ? '' : msg(f, 'admin.overview.vs_last_month_pct', { pct: pct(Math.round(n)) });
  const cards: KpiCard[] = [];

  if (k?.active_subscriptions) {
    const s = k.active_subscriptions;
    cards.push({
      id: 'active_subscriptions',
      icon: 'card',
      label: f.t('admin.overview.kpi.subs.label'),
      value: num(f, a.active_subscriptions ?? s.tiers.premium + s.tiers.business_plus),
      sideV: f.t('admin.overview.kpi.subs.side_v'),
      sideS: msg(f, 'admin.overview.kpi.subs.side_s', {
        a: f.t('vocab.plan_tier.premium'),
        b: f.t('vocab.plan_tier.business_plus'),
      }),
      delta: {
        text: msg(f, 'admin.overview.kpi.subs.new', { n: s.new_month }),
        up: s.new_month >= 0,
      },
      note: msg(f, 'admin.overview.kpi.subs.note', { n: s.new_prev_month }),
      stats: [
        {
          v: num(f, s.tiers.premium),
          k: f.t('vocab.plan_tier.premium'),
          s: f.t('admin.overview.kpi.subs.active_paid'),
        },
        {
          v: num(f, s.tiers.business_plus),
          k: f.t('vocab.plan_tier.business_plus'),
          s: f.t('admin.overview.kpi.subs.active_paid'),
        },
        {
          v: num(f, s.tiers.free),
          k: f.t('vocab.plan_tier.free'),
          s: msg(f, 'admin.overview.kpi.subs.new', { n: s.free_new_month }),
        },
      ],
      sub: msg(f, 'admin.overview.kpi.subs.renewals', { n: s.renewals_30d }),
      rate: trendRate(s.new_month),
      spark: seriesOf(a, 'active_subscriptions'),
      money: false,
    });
  }

  const lines = a.revenue_lines ?? [];
  if (lines.length) {
    const total = lines.reduce((x, l) => x + l.month_minor, 0);
    const prev = lines.reduce((x, l) => x + l.prev_minor, 0);
    const p = pctChange(total, prev);
    const line = (key: string) => lines.find((l) => l.key === key);
    const subs = line('subscriptions');
    const promo = line('placement');
    cards.push({
      id: 'revenue',
      icon: 'currency',
      label: f.t('admin.overview.kpi.revenue.label'),
      value: f.money(a.revenue_minor ?? total),
      delta: p === null ? undefined : { text: pct(p), up: p >= 0 },
      note: p === null ? undefined : f.t('admin.overview.vs_last_month'),
      stats: [
        ...(subs
          ? [
              {
                v: f.money(subs.month_minor),
                k: f.t('admin.overview.kpi.revenue.subs'),
                s: vsLast(pctChange(subs.month_minor, subs.prev_minor)),
              },
            ]
          : []),
        ...(promo
          ? [
              {
                v: f.money(promo.month_minor),
                k: f.t('admin.overview.kpi.revenue.promo'),
                s: vsLast(pctChange(promo.month_minor, promo.prev_minor)),
              },
            ]
          : []),
      ],
      sub: f.t('admin.overview.kpi.revenue.sub'),
      rate: trendRate(p),
      spark: seriesOf(a, 'revenue'),
      money: true,
    });
  }

  if (k?.bookings) {
    const b = k.bookings;
    cards.push({
      id: 'bookings',
      icon: 'calendar',
      label: f.t('admin.overview.kpi.bookings.label'),
      value: num(f, b.today),
      sideV: num(f, b.month),
      sideS: f.t('admin.overview.kpi.bookings.side_s'),
      delta: { text: pct(b.today_delta_pct), up: b.today_delta_pct >= 0 },
      note: f.t('admin.overview.kpi.bookings.note'),
      stats: [
        {
          v: f.money(b.booked_month_minor),
          k: f.t('admin.overview.kpi.bookings.booked'),
          s: vsLast(pctChange(b.booked_month_minor, b.booked_prev_month_minor)),
        },
        {
          v: f.money(b.booked_ytd_minor),
          k: f.t('admin.overview.kpi.bookings.ytd'),
          s: yearToDate(f),
        },
      ],
      rate: trendRate(b.today_delta_pct),
      spark: seriesOf(a, 'bookings'),
      money: false,
    });
  }

  if (k?.active_businesses) {
    const b = k.active_businesses;
    const cats = a.category_split ?? [];
    const catName = (i: number) => {
      const c = cats[i];
      return c ? (f.lang === 'bn' ? c.label_bn : c.label_en) : '';
    };
    cards.push({
      id: 'active_businesses',
      icon: 'store',
      label: f.t('admin.overview.kpi.biz.label'),
      value: num(f, b.active),
      sideV: num(f, b.using_bookings),
      sideS: f.t('admin.overview.kpi.biz.side_s'),
      delta: { text: signed(f, b.delta, 0), up: b.delta >= 0 },
      note: f.t('admin.overview.vs_last_month'),
      stats: [
        {
          v: f.lang === 'bn' ? b.top_city.name_bn : b.top_city.name_en,
          k: f.t('admin.overview.kpi.biz.top_city'),
          s: msg(f, 'admin.overview.kpi.biz.top_city_n', { n: b.top_city.count }),
        },
        {
          v: num(f, b.categories_live),
          k: f.t('admin.overview.kpi.biz.categories'),
          s:
            cats.length >= 2
              ? msg(f, 'admin.overview.kpi.biz.categories_s', {
                  a: catName(0),
                  b: catName(1),
                  n: Math.max(0, b.categories_live - 2),
                })
              : '',
        },
        {
          v: num(f, b.pending_approval),
          k: f.t('admin.overview.kpi.biz.pending'),
          s: f.t('admin.overview.kpi.biz.pending_s'),
        },
      ],
      rate: trendRate(b.delta),
      spark: seriesOf(a, 'active_businesses'),
      money: false,
    });
  }

  if (k?.active_offers) {
    const o = k.active_offers;
    cards.push({
      id: 'active_offers',
      icon: 'tag',
      label: f.t('admin.overview.kpi.offers.label'),
      value: num(f, o.live),
      sideV: num(f, o.live),
      sideS: f.t('admin.overview.kpi.offers.side_s'),
      delta: { text: signed(f, o.delta, 0), up: o.delta >= 0 },
      note: f.t('admin.overview.kpi.offers.note'),
      stats: [
        {
          v: num(f, o.served_month),
          k: f.t('admin.overview.kpi.offers.served_month'),
          s: msg(f, 'admin.overview.kpi.offers.served_month_s', {
            n: signed(f, o.served_month_delta, 0),
          }),
        },
        {
          v: num(f, o.served_year),
          k: f.t('admin.overview.kpi.offers.served_year'),
          s: yearToDate(f),
        },
      ],
      rate: trendRate(o.delta),
      spark: seriesOf(a, 'active_offers'),
      money: false,
    });
  }

  if (k?.gmv) {
    const g = k.gmv;
    const p = pctChange(g.gmv_minor, g.gmv_prev_minor);
    cards.push({
      id: 'gmv',
      icon: 'chart',
      label: f.t('admin.overview.kpi.gmv.label'),
      value: f.money(g.gmv_minor),
      sideV: num(f, g.completed_month),
      sideS: f.t('admin.overview.kpi.gmv.side_s'),
      delta: p === null ? undefined : { text: pct(p), up: p >= 0 },
      note: f.t('admin.overview.kpi.gmv.note'),
      stats: [
        {
          v: `${num(f, g.repeat_rate)}%`,
          k: f.t('admin.overview.kpi.gmv.repeat'),
          s: f.t('admin.overview.kpi.gmv.repeat_s'),
        },
        {
          v: `${num(f, g.on_platform_share)}%`,
          k: f.t('admin.overview.kpi.gmv.on_platform'),
          s: f.t('admin.overview.kpi.gmv.on_platform_s'),
        },
        {
          v: `${num(f, g.directory_covered)}/${num(f, g.directory_total)}`,
          k: f.t('admin.overview.kpi.gmv.directory'),
          s: f.t('admin.overview.kpi.gmv.directory_s'),
        },
      ],
      rate: trendRate(p),
      spark: seriesOf(a, 'gmv'),
      money: true,
    });
  }
  return cards;
}
